import { useEffect, useState } from "react";
import treeEPCService from "../../../../services/treeEPCService";
import { useViewerStore } from "../../../../store/store";
import { Button, Checkbox, Flex, Input, Spin, Tooltip, Typography } from "antd";
import './TreeEPCStyle.scss'
import { Warning } from "@phosphor-icons/react";
import { getSunnovaData } from "../../../../services/sunnovaDataService";
import SliderInput from "../../../Pure-UI-Components/SliderInput/SliderInput";
import { useCalculationState } from "../../../../hooks/calculationStateHook";
import { MODULE_GRAPH } from "../../../../services/siteCalculationState";
import { GetRoundNum } from "../Three-JS-Viewer/Controllers/Common";
import { EPCStep } from "../../../../@types/enums";
import Btn from "../../../Pure-UI-Components/Button/Button";

const { Text, Link } = Typography;

const TreeEPCForm = () => {

    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const escalatorValue = useViewerStore(state => state.escalatorValue);

    const {
        totalSystemSize,
        treeCartItems,
        multiplier,
        epcPriceData,
        ppwValue,
        epcValue,
        freezePPW,
        initialPPWReady,
        solarRateSettings,
        designList,
        selectedDesignIndex,
        pricingType,
        annualUsage,
        epcEstimatedProduction,
        epcMessage,
        epcCalculatedPrice
    } = useViewerStore(state => state);

    //EPC Price Status  
    const [epcButtonText, setEpcButtonText] = useState("Get Sunnova Details");
    const [currentStepProcessing, setCurrentStepProcessing] = useState(EPCStep.None);
    const [stepStatus, setStepStatus] = useState('');
    const [showEPCErrorButton, setShowEPCErrorButton] = useState(false);
    const [showEPCButton, setShowEPCButton] = useState(true);
    const [oneButtonUrl, setOneButtonUrl] = useState('');
    const [treeCartTotalCost, setTreeCartTotalCost] = useState(0);
    const [solarRate, setSolarRate] = useState(0)
    const [sliderMarks, setSliderMarks] = useState({})
    const [isPPWupdate, setIsPPWupdate] = useState(false)
    const [multiplierUp, setIsMultiplierUp] = useState(false);
    const [epcValueLoading, setEpcValueLoading] = useState(false);
    const [epcAPIStatus, setEpcAPIStatus] = useState("isLoading");

    //API Calls Timer
    const [apiStartedTime, setApiStartedTime] = useState(0);
    const [time, setTime] = useState(0);

    const priceFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    const productionFormatter = new Intl.NumberFormat('en-US');

    const setFreezePPW = (v: boolean) => useViewerStore.setState({ freezePPW: v });
    const freezePPWHandler = (e: any) => setFreezePPW(e.target.checked);

    useCalculationState(MODULE_GRAPH, { useCalculation: true });

    useEffect(() => {
        navigateToOneButton();
        checkOpportunityEPCStatus();
        if (ppwValue !== 0 && solarRate === 0) {
            setSolarRate(findRate(ppwValue));
        }
        handlePriceBookingAPI();
    }, []);

    useEffect(() => {
        if (escalatorValue !== undefined) {
            handlePriceBookingAPI();
        }
    }, [escalatorValue])


    useEffect(() => {
        setTreeCartTotalCost(0);
        // setCartCount(treeCartItems ? treeCartItems.length : 0);
        recalculateTreeCartTotal();
    }, [treeCartItems])

    const recalculateTreeCartTotal = () => {
        let total = 0;
        treeCartItems?.forEach((cartTree) => {
            total += cartTree.userData.treeCartInfo?.total || 0;
        });
        setTreeCartTotalCost(total);
    }


    useEffect(() => {
        if (multiplier === 0) return;
        setIsMultiplierUp(false);
        if (epcPriceData.length === 0) {
            setIsPPWupdate(false);
            handlePriceBookingAPI();
        } else if (epcPriceData.length > 0 && multiplier !== epcPriceData[0].Multiplier) {
            if (freezePPW && multiplier > epcPriceData[0].Multiplier) {
                sceneViewer.viewerAlert({
                    show: true,
                    title: "Info",
                    message: "The PPW has been unfrozen as we have the better PPW",
                    messageType: "info",
                    isModal: false,
                });
                setIsMultiplierUp(true);
            }
            setIsPPWupdate(true);
            handlePriceBookingAPI();
        }
        fetchEpcEstimate();
    }, [multiplier, selectedDesignIndex])

    //Price Booking API
    const handlePriceBookingAPI = async () => {
        setEpcValueLoading(true);
        setEpcAPIStatus("isLoading");
        if (multiplier === 0) return;
        const state = sessionStorage.getItem('stateCode');
        const Utility_Company = sessionStorage.getItem('Utility_Company');
        if (!state || !Utility_Company) return;

        const escalator = escalatorValue || 0;
        const financeMethod = pricingType || '';
        const priceBookingData = await treeEPCService.priceBooking(state, Utility_Company, multiplier, escalator, financeMethod);
        if (priceBookingData !== "No records found") {
            useViewerStore.setState({ epcPriceData: priceBookingData });
            console.log(priceBookingData)
            const minRate = priceBookingData[0].Rate;
            const maxRate = priceBookingData[priceBookingData.length - 1].Rate
            useViewerStore.setState({
                solarRateSettings: {
                    min: minRate,
                    max: maxRate,
                    step: 0.001
                }
            });
            setEpcAPIStatus("isSuccess");
            setSolarRate(Number((minRate + (0.35 * (maxRate - minRate))).toFixed(3)));
        } else {
            useViewerStore.setState({ solarRateSettings: undefined });
            setEpcAPIStatus("isError");
        }
        setEpcValueLoading(false)
    }

    useEffect(() => {
        if (solarRateSettings && solarRate) {
            setSliderMarks({
                [solarRate]: {
                    style: {
                        fontSize: '12px',
                        marginTop: '-32px'
                    },
                    label: solarRate,
                },
                [solarRateSettings.min]: {
                    style: {
                        fontSize: '12px',
                        marginTop: '2px'
                    },
                    label: solarRateSettings.min,
                },
                [solarRateSettings.max]: {
                    style: {
                        fontSize: '12px',
                        marginTop: '2px'
                    },
                    label: solarRateSettings.max,
                }
            });
            // Update PPW initially or if multiplier increased or if not freezed
            let ppw = !initialPPWReady || multiplierUp || !freezePPW ? findPPW(solarRate) : ppwValue;
            if (!initialPPWReady) {
                // Set default freezePPW after setting initial PPW
                useViewerStore.setState({ initialPPWReady: true, freezePPW: true });
            }
            let epcPrice = GetRoundNum(((ppw * (totalSystemSize * 1000)) + treeCartTotalCost), 3);
            useViewerStore.setState({ ppwValue: ppw, epcValue: epcPrice });
        }
    }, [solarRateSettings, solarRate, treeCartTotalCost])

    useEffect(() => {
        if (freezePPW && !multiplierUp) {
            setSolarRate(findRate(GetRoundNum(ppwValue, 2)));
        } else if (isPPWupdate) {
            const newPPW = epcValue / (totalSystemSize * 1000);
            handlePPWvalue(newPPW);
        } else {
            setSolarRate(findRate(GetRoundNum(ppwValue, 2)));
        }
        setIsMultiplierUp(false);
    }, [epcValue])

    const handlePPWvalue = (newPPW: number) => {
        setSolarRate(findRate(GetRoundNum(newPPW, 2)));
        setIsPPWupdate(false);
    }


    const findPPW = (rate: number) => {
        if (!epcPriceData || !epcPriceData.length) return 0;
        const found = epcPriceData?.find(item => item.Rate === rate);
        return found ? found.PPW : 0;
    }
    const findRate = (givenPPW: number) => {
        for (let i = 0; i < epcPriceData.length; i++) {
            if (epcPriceData[i].PPW >= givenPPW) {
                return epcPriceData[i].Rate;
            }
        }
        return solarRateSettings?.max; // Return null if no larger PPW value is found
    };


    //OBA EPC Pricing Calculation
    const checkOpportunityEPCStatus = async () => {
        setTime(0);
        setApiStartedTime(new Date().getTime());
        setShowEPCErrorButton(false);
        setCurrentStepProcessing(EPCStep.None);
        setStepStatus("Initializing...");
        setShowEPCButton(false);
        const salesforceid = sessionStorage.getItem('SalesForceId');
        if (!salesforceid) return;
        const epcStatus = await treeEPCService.getOpportunityStatusForEPCPricing(salesforceid);
        bindEPCStatus(epcStatus);
    };

    const bindEPCStatus = async (epcStatus: any) => {

        //If Error
        if (epcStatus?.code && epcStatus?.code === 'ERR_BAD_REQUEST') {
            useViewerStore.setState({ epcMessage: epcStatus?.response?.data?.Message });
            setEpcButtonText("Get Sunnova Details");
            autoEPCPricing(EPCStep.Lead, false);
        } else {

            //If Annual Usage is retrieved
            const annualUsage = epcStatus?.UsageValue;

            //Annual Usage from oneBUTTON
            useViewerStore.setState({ annualUsage: annualUsage });

            //Sync Status - Lead, Contact, Utility, Design, Quote
            const syncStatusObject = epcStatus?.syncStatusKeyValue;

            if (syncStatusObject) {
                let readyForLeadSystem = syncStatusObject?.find((a: any) => a.key === 'ReadyForLeadSystem' && a.value === true);
                let currentStep = syncStatusObject?.find((a: any) => a.value === false);
                autoEPCPricing(readyForLeadSystem ? EPCStep.ReadyForLeadSystem : currentStep["key"], readyForLeadSystem);
            } else {
                setEpcButtonText("Get Sunnova Details");
            }
        }
    }

    useEffect(() => {
        const interval = setInterval(() => setTime(GetRoundNum((Date.now() - apiStartedTime) / 1000)), 1000);
        return () => clearInterval(interval); // Clear timer on dispose
    }, [apiStartedTime]);

    const autoEPCPricing = async (step: EPCStep, completed_status: boolean) => {
        setTime(0);
        setApiStartedTime(new Date().getTime());

        setCurrentStepProcessing(step);
        if (step === EPCStep.Lead && completed_status === false) {
            setStepStatus("Creating Sunnova Lead");
            await createEPCPricing(step);
        }
        if (step === EPCStep.Contact && completed_status === false) {
            setStepStatus("Creating Sunnova Contact");
            await createEPCPricing(step);
        }
        if (step === EPCStep.Utility && completed_status === false) {
            setStepStatus("Syncing Utility Information");
            await createEPCPricing(step);
        }
        if (step === EPCStep.ReadyForLeadSystem && completed_status === true) {
            setShowEPCButton(true);
            setEpcButtonText("Run Quote");
        }
        if (step === EPCStep.Design && completed_status === false) {
            setStepStatus("Updating Design");
            await createEPCPricing(step);
        }
        if (step === EPCStep.Quote && completed_status === false) {
            setStepStatus("Creating Quote");
            await createEPCPricing(step);
        }
    }

    const createEPCPricing = async (step: EPCStep) => {
        setShowEPCButton(false);
        setCurrentStepProcessing(step);
        const sales_force_id = sessionStorage.getItem('SalesForceId');
        const epcPricingObject = {
            salesforceId: sales_force_id,
            stepName: step,
            createLeadSystem: step === EPCStep.Design ? true : false,
            pricingMethod: pricingType,
            pricingValue: ppwValue,
            annualUsage: annualUsage ? Number(annualUsage) : 0,
            escalator: escalatorValue ? Number(escalatorValue) : 0,
            epcDesignData: getSunnovaData()
        }


        if (epcPricingObject.createLeadSystem == true && (epcPricingObject.pricingValue === 0 || epcPricingObject.annualUsage === 0)) {
            sceneViewer.viewerAlert({
                show: true,
                title: 'Invalid details',
                message: 'Please check Annual Usage and Pricing Value.',
                messageType: "error",
                isModal: false,
            });
            setShowEPCButton(true);
            return;
        }

        // Update EPC Price Response
        const epcResponse = await treeEPCService.createEPCQuote(epcPricingObject);
        // setEpcPriceResponse(epcResponse);

        //Check for Error Response creating a quote
        if (epcResponse?.code && epcResponse?.code === 'ERR_BAD_REQUEST') {

            if (epcResponse.response.data.ExceptionMessage != null) {
                useViewerStore.setState({ epcMessage: epcResponse.response.data.ExceptionMessage.Message });
            } else {
                if (epcResponse?.response.data == "") {
                    useViewerStore.setState({ epcMessage: "Unknown Error" });
                } else {
                    const errorMessage = epcResponse?.response.data?.StepStatus?.find((a: any) => a.Error !== '' || a.Error != null);
                    useViewerStore.setState({ epcMessage: errorMessage.Error });
                }
            }
            setShowEPCButton(true);
            setShowEPCErrorButton(true);
            navigateToOneButton();
            return;
        } else {
            //Check the next Step to run
            const nextStep = epcResponse?.StepStatus[0];

            //No Next Steps
            if (nextStep?.NextStep == null) {
                if (nextStep?.Error != null || nextStep?.Error != "") {
                    useViewerStore.setState({ epcMessage: nextStep?.Error });
                    setShowEPCErrorButton(true);
                    navigateToOneButton();
                } else {
                    setShowEPCErrorButton(false);
                }
            }

            if (epcResponse?.EPCPriceValue) {
                //If trees are ghosted and added to the cart, update total epc with the trees
                // if(cartCount === 0){
                //     useViewerStore.setState({epcCalculatedPrice: priceFormatter.format(epcResponse?.EPCPriceValue)});
                // } else {
                //     useViewerStore.setState({totalSunnovaEPCWithTrees: priceFormatter.format(epcResponse?.EPCPriceValue)});
                // }

                useViewerStore.setState({
                    epcCalculatedPrice: epcResponse?.EPCPriceValue,
                    epcEstimatedProduction: epcResponse?.EstimatedProduction
                });
                setShowEPCErrorButton(false);
            }

            //Start Next Step
            if (nextStep?.NextStep && nextStep?.NextStep != null) {
                if (nextStep?.NextStep === 'Design') {
                    navigateToOneButton();
                    checkOpportunityEPCStatus();
                } else {
                    await autoEPCPricing(nextStep.NextStep, false);
                }
            }

            setShowEPCButton(true);
        }
    }

    const showErrorMessage = () => {
        navigateToOneButton();
        sceneViewer.viewerAlert({
            show: epcMessage !== "" ? true : false,
            title: currentStepProcessing + ' Error',
            message: epcMessage,
            messageType: "error",
            isModal: false,
        });
    }


    const fetchEpcEstimate = async () => {
        const sales_force_id = sessionStorage.getItem('SalesForceId');
        if (!sales_force_id || !designList.length || selectedDesignIndex === -1) return;
        const selectedDesign = designList[selectedDesignIndex];
        const design_name = `Design:${selectedDesign.Design_No}-Version:${selectedDesign.Version_No}`;
        treeEPCService.getEpcEstimation(sales_force_id, design_name).then(data => {
            if (!data || data.Message === "No records found") {
                useViewerStore.setState({
                    pricingType: "PPW",
                    pricingValue: "",
                    escalatorValue: escalatorValue,
                    epcEstimatedProduction: null,
                    epcCalculatedPrice: "- Quote Not Run -"
                });
                return;
            }
            // setPricingValue(data["Pricing_Value"])
            useViewerStore.setState({
                pricingType: data.Pricing_Method ?? "PPW",
                pricingValue: data.Pricing_Value ?? "",
                annualUsage: data.Annual_Usage ?? 0,
                epcCalculatedPrice: epcCalculatedPrice ?? "- Quote Not Run -",
                escalatorValue: escalatorValue ?? 0,
                epcEstimatedProduction: epcEstimatedProduction,
            });

        });
    }

    const navigateToOneButton = () => {
        let oneButtonUrl = `${import.meta.env.VITE_ONE_BUTTON_LINK}`;
        let salesForceID = sessionStorage.getItem('SalesForceId');
        if (currentStepProcessing === "Design" || currentStepProcessing === "ReadyForLeadSystem") {
            setOneButtonUrl(oneButtonUrl + 'system-design/' + salesForceID);
        } else if (currentStepProcessing === "Utility") {
            setOneButtonUrl(oneButtonUrl + 'utility/' + salesForceID);
        } else {
            setOneButtonUrl(oneButtonUrl + 'opportunity_info/' + salesForceID);
        }
    }

    return (
        <>
            <div className='tab__column' style={{ paddingTop: "8px" }}>
                {

                    epcAPIStatus === "isLoading" ?
                        <>
                            <Flex justify={'center'} align={'center'} gap={10}>
                                <span className="loader-icon-spinner" style={{ width: "36px", height: "36px", borderWidth: "4px" }}></span>
                                <Text type="warning">Loading price data</Text>
                            </Flex>
                        </>
                        : epcAPIStatus === "isError" ?
                            <>
                                <Button
                                    type="dashed"
                                    icon={<Warning size={18} />}
                                    danger
                                    className="full-width no-click"
                                >
                                    Price rate records are not found
                                </Button>
                            </>
                            :
                            epcAPIStatus === "isSuccess" && (
                                <>
                                    {
                                        solarRateSettings && (
                                            <>
                                                <div className='tab__flex justify-between mb-0' style={{ alignItems: "center" }}>
                                                    <Tooltip title="kWh rate to use on the solar quote. " className="tab__column--subtitle mb-1"
                                                        style={{ width: "20%" }}>Rate :</Tooltip>
                                                    <div className="colorSlider" style={{ width: "80%" }}>
                                                        <SliderInput
                                                            settings={solarRateSettings}
                                                            value={solarRate}
                                                            onChange={!freezePPW && setSolarRate}
                                                            sliderMarks={sliderMarks}
                                                            hideInput={true}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='tab__flex justify-between mb-2 mt-1'>
                                                    <Tooltip title="Price Per Watt to use on the solar quote">
                                                        <span className='tab__column--subtitle mb-0'>
                                                            <label htmlFor="ProductionInput">PPW :  ${ppwValue}</label>
                                                        </span>
                                                    </Tooltip>

                                                    <Tooltip title="Freeze the PPW(Price Per Watt) value (disables Rate slider)">
                                                        <Checkbox checked={freezePPW} onChange={freezePPWHandler}>
                                                            Freeze PPW
                                                        </Checkbox>
                                                    </Tooltip>
                                                </div>
                                                <div className='tab__flex justify-between mb-1'>
                                                    <Tooltip title="Total cost to use on the solar quote">
                                                        <span className='tab__column--subtitle mb-0'>
                                                            <label htmlFor="ProductionInput">EPC : {epcValueLoading ?
                                                                <Spin /> : priceFormatter.format(epcValue)}</label>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Efficiency ratio between system size and production">
                                                        <span className='tab__column--subtitle mb-0'>
                                                            <label htmlFor="ProductionInput">Multiplier : {multiplier}</label>
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                                <span className='tab__flex' style={{ alignContent: "center" }}>
                                                    <Btn className="no-click" style={{ alignContent: "center" }}>
                                                        <small className="tab__column--subtitle" style={{ fontSize: "11px", marginBottom: "0px" }}>Note: EPC is based off selected escalator {escalatorValue}</small>
                                                    </Btn>
                                                </span>
                                            </>
                                        )

                                    }

                                </>
                            )
                }

            </div>



            <div className="tab__column epc-content">
                <div className={'treeEPCForm'}>
                    <div className='tab__flex justify-between mb-1'>
                        <span className='tab__column--subtitle mb-0'>
                            <label>Utility Company: {sessionStorage.getItem("Utility_Company")}</label>
                        </span>
                    </div>
                    <Tooltip title="Current yearly energy consumption" style={{ paddingTop: "5px" }}>
                        <span className='tab__column--subtitle mb-0'>
                            <label>Annual Usage (kW): {annualUsage?.toLocaleString()} </label>
                        </span>
                    </Tooltip>

                    {/* EPC PRICING */}
                    {
                        showEPCButton == true ?
                            <Btn
                                id={"submitEPCInfoBtn"}
                                variant="btn-primary"
                                size="btn-md"
                                className="editor__control--finalize mt-1 mb-0"
                                onClick={epcButtonText === 'Run Quote' ? (e => autoEPCPricing(EPCStep.Design, false)) : checkOpportunityEPCStatus}
                            > {epcButtonText}
                            </Btn>
                            :
                            <>
                                <Flex justify={'center'} align={'center'} gap={10} className="mt-1">
                                    <span className="loader-icon-spinner"
                                        style={{ width: "36px", height: "36px", borderWidth: "4px" }}></span>

                                    <Text type="warning">{stepStatus}</Text>

                                </Flex>
                                <Flex justify={'center'} align={'center'} gap={10}>
                                    <Text type="warning" style={{ display: "block" }}>{time} Seconds</Text>
                                </Flex>
                            </>
                    }

                    {/* Error Message  */}
                    {
                        showEPCErrorButton && (
                            <>
                                <Button
                                    type="dashed"
                                    icon={<Warning size={18} />}
                                    onClick={() => showErrorMessage()}
                                    className="mt-1"
                                    danger>{currentStepProcessing} Error</Button>
                            </>

                        )
                    }
                    <br />
                    <Btn 
                        id={"oneButtonURL"}
                        variant="btn-primary"
                        size="btn-md"
                        onClick={() => window.open(oneButtonUrl, "_blank")}
                        className="editor__control--finalize"
                    > Go To One Button
                    </Btn>
                </div>
            </div>
            <div className='tab__column'>
                <Tooltip title="Sunnova EPC amount for the solar system and quote" className='tab__flex justify-between mb-2'>
                    <span className='tab__column--subtitle mb-0'>Sunnova EPC:</span>
                    <span className='tab__column--subtitle mb-0'>
                        {epcCalculatedPrice === "- Quote Not Run -" 
                            ? "- Quote Not Run -" 
                            : priceFormatter.format(typeof epcCalculatedPrice === "number" ? epcCalculatedPrice : 0)}
                    </span>
                </Tooltip>
                <Tooltip title="Sunnova's production estimate for the solar system" className='tab__flex justify-between mb-2'>
                    <span className='tab__column--subtitle mb-0'>Production Estimate:</span>
                    <span className='tab__column--subtitle mb-0'>{productionFormatter.format(epcEstimatedProduction ?? 0)} W</span>
                </Tooltip>
                <Tooltip title="Total cost of removing the trees in your cart">
                    <div className='tab__flex justify-between mb-2'>
                        <span className='tab__column--subtitle mb-0'>Tree Cart Total:</span>
                        <span className='tab__column--subtitle mb-0'>{priceFormatter.format(treeCartTotalCost)}</span>
                    </div>
                </Tooltip>
                <div className='tab__flex justify-between'>
                    <span className='tab__column--subtitle mb-0'>System Size:</span>
                    <span className='tab__column--subtitle mb-0'>{((Number(totalSystemSize)).toFixed(2))?.toLocaleString()} kW</span>
                </div>
            </div>
        </>

    )
}


export default TreeEPCForm;
